import React, { useState, useEffect } from 'react'
import { Table, Button, Tag, Space, Input, DatePicker, Select } from 'antd'
import { SearchOutlined, DollarOutlined } from '@ant-design/icons'
import moment from 'moment'
import { getUnallocatedPayments } from '../../backend-integration/api/paymentAllocation'
import { BillingEnums } from '../../utils/enums'

const { RangePicker } = DatePicker
const { Option } = Select

const UnallocatedPaymentsList = ({ onAllocate, refreshKey }) => {
  const [loading, setLoading] = useState(false)
  const [payments, setPayments] = useState([])
  const [filters, setFilters] = useState({})
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    fetchPayments()
  }, [pagination.current, pagination.pageSize, filters, refreshKey])

  const fetchPayments = async () => {
    setLoading(true)
    try {
      const { data, total } = await getUnallocatedPayments({
        ...filters,
        page: pagination.current,
        limit: pagination.pageSize
      })
      setPayments(data)
      setPagination((prev) => ({ ...prev, total }))
    } catch (error) {
      console.error('Error fetching unallocated payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleDateFilter = (dates) => {
    if (dates) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].toDate(),
        endDate: dates[1].toDate()
      }))
    } else {
      setFilters((prev) => {
        const { startDate, endDate, ...rest } = prev
        return rest
      })
    }
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const getAgeColor = (days) => {
    if (days > 30) return 'error'
    if (days > 14) return 'warning'
    if (days > 7) return 'processing'
    return 'success'
  }

  const columns = [
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => moment(date).format('YYYY-MM-DD'),
      sorter: true
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <a>{text}</a>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `R ${amount.toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => (
        <Tag color={source === 'bank_feed' ? 'blue' : 'green'}>
          {source === 'bank_feed' ? 'Bank Feed' : 'Manual Entry'}
        </Tag>
      )
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Age (Days)',
      dataIndex: 'ageInDays',
      key: 'ageInDays',
      render: (days) => <Tag color={getAgeColor(days)}>{days} days</Tag>,
      sorter: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type='primary' size='small' icon={<DollarOutlined />} onClick={() => onAllocate(record)}>
          Allocate
        </Button>
      )
    }
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder='Search by reference or description'
          onSearch={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
        />
        <RangePicker onChange={handleDateFilter} format='YYYY-MM-DD' />
        <Select
          placeholder='Payment Method'
          style={{ width: 150 }}
          allowClear
          onChange={(value) => setFilters((prev) => ({ ...prev, paymentMethod: value }))}
        >
          {Object.values(BillingEnums.paymentMethods).map((method) => (
            <Option key={method} value={method}>
              {method}
            </Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={payments}
        rowKey='_id'
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} unallocated payments`,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize
            }))
          }
        }}
      />
    </>
  )
}

export default UnallocatedPaymentsList
